import { Checkbox, Form, InputNumber } from 'antd';
import { useContext } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { RULES } from 'app/components/pages/Hotels/HotelDetail/Finance/utils';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

export const GuestConfiguration = () => {
  const { isViewMode, isEditMode } = useContext(ViewEditModeContext);

  const form = Form.useFormInstance();
  const minValue = Form.useWatch('minPax', form);
  const maxValue = Form.useWatch('maxPax', form);

  const priceDifferentiationMode = Form.useWatch(
    ['openingConfiguration', 'priceConfiguration', 'priceDifferentiationMode'],
    form
  );

  const handleAllowedPaxChange = (
    selectedPaxTypes: (string | number | boolean)[]
  ) => {
    const stringPaxTypes = selectedPaxTypes.filter(
      (val): val is string => typeof val === 'string'
    );

    if (!stringPaxTypes || stringPaxTypes.length === 0) {
      return;
    }

    const hasAdultOnly =
      stringPaxTypes.length === 1 && stringPaxTypes[0] === PAX_TYPE.ADULT;
    const hasChildOnly =
      stringPaxTypes.length === 1 && stringPaxTypes[0] === PAX_TYPE.CHILD;

    if (
      (hasAdultOnly || hasChildOnly) &&
      (priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
        priceDifferentiationMode ===
          PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME)
    ) {
      const newMode =
        priceDifferentiationMode ===
        PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
          ? PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME
          : PRICE_DIFFERENTIATION_MODE.NONE;

      const currentConfig =
        form.getFieldValue([
          'openingConfiguration',
          'priceConfiguration',
          'configuration',
        ]) || {};

      form.setFieldsValue({
        openingConfiguration: {
          priceConfiguration: {
            priceDifferentiationMode: newMode,
            configuration: {
              ...currentConfig,
              childPrice: hasChildOnly ? currentConfig.childPrice : undefined,
              childDaytimePrice: hasChildOnly
                ? currentConfig.childDaytimePrice
                : undefined,
              price: hasAdultOnly ? currentConfig.price : undefined,
              daytimePrice: hasAdultOnly
                ? currentConfig.daytimePrice
                : undefined,
            },
          },
        },
      });
    }
  };

  return (
    <SCard
      title="Guests"
      subtitle="Define which guests can access the activity, and with what restrictions."
      className="experience-form guests-configuration"
    >
      <FormLineLayout isRequired label="Allowed guests" disabled={isViewMode}>
        <Form.Item name="allowedPaxTypes" rules={RULES}>
          <Checkbox.Group
            disabled={isViewMode}
            onChange={handleAllowedPaxChange}
          >
            <Checkbox value={PAX_TYPE.ADULT}>Adults</Checkbox>
            <Checkbox value={PAX_TYPE.CHILD}>Children</Checkbox>
          </Checkbox.Group>
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout
        isOptional
        label="Min. guests"
        description="Set a min. guest when the price of the activity is for minimum x people."
        disabled={isViewMode}
      >
        <Form.Item name="minPax">
          {isEditMode ? (
            <InputNumber type="number" min={0} />
          ) : (
            <span className="view-data">{minValue || 'ø empty'}</span>
          )}
        </Form.Item>
      </FormLineLayout>
      <FormLineLayout
        isOptional
        label="Max. guests"
        description="Set a max. guest when the price of the activity is for maximum x people."
        disabled={isViewMode}
      >
        <Form.Item name="maxPax">
          {isEditMode ? (
            <InputNumber type="number" min={0} />
          ) : (
            <span className="view-data">{maxValue || 'ø empty'}</span>
          )}
        </Form.Item>
      </FormLineLayout>
    </SCard>
  );
};
